import React, { useState, useEffect } from "react";
import { SketchPicker } from "react-color";
import { createResource, updateResource, validateResource } from '../../api/resourceMngApi';
import OrgSelect from '../../components/OrgSelect/OrgSelect';
import "./ResourceForm.css";

export default function ResourceForm({ mode = "create", resource, token, comCode, onCancel, onSuccess }) {
  
  const [resType, setResType] = useState("자원");
  const [resCategory, setResCategory] = useState("회의실");
  const [resColor, setResColor] = useState("#1edc71");
  const [resName, setResName] = useState("");
  const [sortOrder, setSortOrder] = useState(1);
  const [description, setDescription] = useState("");
  const [useYn, setUseYn] = useState("Y");
  const [parentId, setParentId] = useState("");
  // ✅ 관리자 정보 필드명 변경
  const [approverId, setApproverId] = useState("");
  const [approverName, setApproverName] = useState("");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showOrgSelect, setShowOrgSelect] = useState(false);
  const [saving, setSaving] = useState(false);

  const [categories, setCategories] = useState([]);

  // 🔑 edit 모드일 때 기존 데이터 반영
  useEffect(() => {
    if (mode === "edit" && resource) {
      setResType(resource.res_type || "자원");
      setResCategory(resource.res_category || "회의실");
      setResColor(resource.res_color || "#1edc71");
      setResName(resource.res_name || "");
      setSortOrder(resource.sort_order || 1);
      setDescription(resource.description || "");
      setUseYn(resource.use_yn || "Y");
      setParentId(resource.parent_id || "");
      // ✅ 관리자 정보 반영

      setApproverId(resource.approver_id || "");
      setApproverName(resource.approver_name || "");

    }
  }, [mode, resource]);

  useEffect(() => {
    
    if (resType === "자원") {
      setResCategory("회의실");
    } else {
      setResCategory("");
      setResColor("#1edc71");
      setDescription("");
      // ✅ 카테고리일 때 관리자 값 초기화
      setApproverId("");
      setApproverName("");
    }
  }, [resType]);

  useEffect(() => {
    if (!comCode) {
      setCategories([]);
      return;
    }
    const fetchCategories = async () => {
      try {
        const res = await fetch('/api/resources/categories', {
          headers: { Authorization: `Bearer ${token}`, 'x-com-code': comCode }
        });
        if (!res.ok) {
          throw new Error(`카테고리 조회 실패 (${res.status})`);
        }
        const data = await res.json();
        const normalized = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        setCategories(normalized);
      } catch (err) {
        console.error("카테고리 조회 실패:", err);
        setCategories([]);
      }
    };
    fetchCategories();
  }, [token, comCode]);

  const handleColorChange = (color) => {
    setResColor(color.hex);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      parent_id: parentId ? Number(parentId) : 0,
      res_type: resType,
      res_color: resColor || "#1edc71",
      res_category: resCategory || null,
      res_name: resName,
      sort_order: Number(sortOrder) || 1,
      description: description || "",
      use_yn: useYn || "Y",
      // ✅ 관리자 정보 저장 시 approver_* 필드 사용
      //approver_id: resType === "자원" ? approverId || null : null,
      //approver_name: resType === "자원" ? approverName || "" : ""
      admin_id: resType === "자원" ? approverId || null : null, 
      admin_name: resType === "자원" ? approverName || "" : ""
    };

    const errorMsg = validateResource(payload);
    
    if (errorMsg) {
      alert(errorMsg);
      return;
    }

    try {
      setSaving(true);
      let saved;
      if (mode === "create") {
        saved = await createResource(payload, token, comCode);
      } else if (mode === "edit" && resource) {
        saved = await updateResource(resource.id, payload, token, comCode);
      }

      alert("자원이 저장되었습니다");
      if (onSuccess) onSuccess(saved);
      if (onCancel) onCancel();

    } catch (err) {
      console.error("저장 실패:", err);
      alert("저장 중 오류가 발생했습니다");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="resource-form" onSubmit={handleSubmit}>
      <h2>{mode === "create" ? "자원관리 등록" : "자원관리 수정"}</h2>

      {/* 유형 */}
      <div className="form-group">
        <label>유형</label>
        <div className="inline">
          <label>
            <input
              type="radio"
              checked={resType === "자원"}
              onChange={() => setResType("자원")}
            /> 자원
          </label>
          <label>
            <input            
              type="radio"
              checked={resType === "카테고리"}
              onChange={() => setResType("카테고리")}
            /> 카테고리
          </label>
        </div>
      </div>

      {/* 자원구분 */}
      {resType === "자원" && (
        <div className="form-group">
          <label>자원구분</label>
          <div className="inline">
            {["회의실", "차량", "기타"].map(opt => (
              <label key={opt}>
                <input
                  type="radio"
                  checked={resCategory === opt}
                  onChange={() => setResCategory(opt)}
                /> {opt}
              </label>
            ))}
          </div>
        </div>
      )}

      {/* 상위분류 */}
      <div className="form-group">
        <label>상위분류</label>
        <select className="rf-select" value={parentId} onChange={(e) => setParentId(e.target.value)}>
          <option value="">- 선택 -</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.res_name}</option>
          ))}
        </select>
      </div>

      {/* 자원명 */}
      <div className="form-group">
        <label>자원명</label>
        <div className="inline">
          {resType === "자원" && (
            <div
              className="color-preview"
              style={{
                width: '80px',
                height: '30px',
                backgroundColor: resColor,
                border: '1px solid #ccc',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                color: '#fff',
                marginRight: '8px'
              }}
            >
              Sample Text
            </div>
          )}
          <input
            className="rf-input"
            type="text"
            value={resName}
            onChange={(e) => setResName(e.target.value)}
            placeholder="자원명을 입력하세요"
            style={{ flex: 1 }}
          />
          {resType === "자원" && (
            <button
              type="button"
              className="color-btn"
              onClick={() => setShowColorPicker(!showColorPicker)}
              style={{ marginLeft: '8px' }}
            >
              색상 선택
            </button>
          )}
        </div>
        {resType === "자원" && showColorPicker && (
          <div className="color-layer">
            <div style={{ textAlign: 'right', marginBottom: '6px' }}>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setShowColorPicker(false)}
              >
                닫기
              </button>
            </div>
            <SketchPicker
              color={resColor}
              onChangeComplete={handleColorChange}
            />
          </div>
        )}
      </div>

      {/* 정렬순서 */}
      <div className="form-group">
        <label>정렬순서</label>
        <input
          className="rf-input"
          type="number"
          value={Number(sortOrder)}
          onChange={(e) => {
            const val = Number(e.target.value);
            setSortOrder(Number.isNaN(val) ? 1 : val);
          }}
          step={1}
          min={1}
        />
      </div>

      {/* 설명 */}
      {resType === "자원" && (
        <div className="form-group">
          <label>설명</label>
          <textarea
            className="rf-textarea"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      )}

      {/* 사용여부 */}
      <div className="form-group">
        <label>사용여부</label>
        <select className="rf-select" value={useYn} onChange={(e) => setUseYn(e.target.value)}>
          <option value="Y">Y(사용)</option>
          <option value="N">N(미사용)</option>
        </select>
      </div>

      {/* 관리자 */}
      {resType === "자원" && (
        <div className="form-group">
          <label>관리자</label>
          <div className="inline">
            <span className="selected-admin">
              {approverId ? `선택된 관리자: ${approverName} (${approverId})` : "관리자 미선택"}
            </span>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => setShowOrgSelect(true)}
            >
              조직도에서 선택
            </button>
          </div>
        </div>
      )}

      {resType === "자원" && showOrgSelect && (
        <OrgSelect
          comCode={comCode}
          selectionMode="single"
          targetType="user"
          initialSelected={approverId ? [{
            target_type: "user",
            target_id: approverId,
            target_name: approverName || approverId
          }] : []}
          onConfirm={(selected) => {
            if (selected.length > 0) {
              setApproverId(selected[0].target_id);
              setApproverName(selected[0].target_name);
            }
            setShowOrgSelect(false);
          }}
          onCancel={() => setShowOrgSelect(false)}
        />
      )}

      {/* 액션 버튼 */}
      <div className="d-flex justify-content-end">
        <button type="submit" className="btn btn-primary me-2" disabled={saving}>
          {saving ? '저장 중...' : '저장'}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={saving}
        >
          취소
        </button>
      </div>
    </form>
  );
}